import store from "@/store";
import { getBundleId, getCurrentUserEmail, getLoggedInUserName } from "@/helpers/auth";
import pendingAccountsApi from "./pending-accounts.api";
import type { TPendingAccount } from "@/store/modules/mail/mailbox-nudge.store";

/**
 * Pending mailbox accounts service.
 *
 * Backs the "add mailbox" nudges against the real Control Panel suggestion
 * endpoints (pending-accounts.api). Rows are stored and consumed in the
 * backend's own shape (`TPendingAccount`) — no per-row transformation here or
 * in the store; `get` only unwraps the `{ suggestions: [...] }` envelope.
 *
 * Admin self-adds carry `requesterEmail = current user email` so the store's
 * email-based "added by you" detection (see mailbox-nudge.store `isSelfAdded`)
 * recognises them without a sentinel. Store dispatches happen only after the
 * API resolves, so a failed add/delete never leaves phantom/missing rows.
 */

class PendingAccountsService {
  async fetchPendingAccounts(): Promise<TPendingAccount[]> {
    const data = await pendingAccountsApi.get();
    const rows = Array.isArray(data?.suggestions) ? data.suggestions : [];
    store.dispatch("mail/mailboxNudge/pendingAccounts", rows);
    return rows;
  }

  /**
   * Admin self-add from the success-screen nudge input. Sends a single-item
   * list; `alreadyExists` is treated as success (the store dedupes).
   */
  async addPendingAccount(suggestionEmail: string): Promise<TPendingAccount> {
    await pendingAccountsApi.add({
      suggestedEmails: [suggestionEmail],
      requesterName: getLoggedInUserName(),
      requesterEmail: getCurrentUserEmail(),
      bundleId: getBundleId(),
    });
    const row: TPendingAccount = {
      suggestionEmail,
      requesterEmail: getCurrentUserEmail(),
      requesterName: getLoggedInUserName(),
      status: "Requested",
    };
    store.dispatch("mail/mailboxNudge/addPendingAccount", row);
    return row;
  }

  async removePendingAccount(suggestionEmail: string): Promise<{ success: boolean }> {
    await pendingAccountsApi.remove({
      suggestedEmail: suggestionEmail,
      bundleId: getBundleId(),
    });
    store.dispatch("mail/mailboxNudge/removePendingAccount", suggestionEmail);
    return { success: true };
  }
}

export default new PendingAccountsService();
